import {GoogleGenerativeAI} from '@google/generative-ai'
import * as Toast from '#/view/com/util/Toast'

// Initialize the Gemini API with your API key
const GEMINI_API_KEY = process.env.GEMINI_API_KEY
const genAI = new GoogleGenerativeAI(GEMINI_API_KEY)

const GENAI_PROMPT_VERSION = 'V5'
const GENAI_PROMPT_PART_1 = `
Please help me rewrite the following content to make it more civil and constructive while 
preserving my core message and intent. If I'm responding to someone else's post or comment, 
please incorporate empathetic perspective-taking that acknowledges their viewpoint, uses non-violent 
communication principles, and helps build bridges toward finding common ground. 

  Special considerations:
- Maintain my authentic voice and key points; Keep the text as close as possible to the author’s intent.
- Remove inflammatory language, hate speech, personal attacks, or condescending tones
- Incorporate "I" statements rather than accusatory "you" statements when expressing concerns
- Acknowledge valid points from the other perspective
- Find potential areas of agreement or shared values
- Keep the message concise and focused
- Make the response more likely to foster continued constructive dialogue
- Provide only one option of rewrite
- Do not include quotation marks at the start and end of your suggestion
- If possible, while maintaining the meaning, keep the text to 300 characters
- Do not replace 'cops' with 'law enforcement'  

`
const GENAI_PROMPT_PART_2 = `
My original text:
`
const GENAI_REPLYTO_INSTRUCTION = (replyText?: string) => {
  return `
  
  Context (if responding to someone): "${replyText}".`
}

// Again, this is hacky, but at least allows us to get more parameters into the prompt
// This is the prompt that will be used to generate suggestions
const GENAI_PROMPT = (text: string, replyText?: string) => {
  return `${GENAI_PROMPT_PART_1} ${
    replyText ? GENAI_REPLYTO_INSTRUCTION(replyText) : ''
  } ${GENAI_PROMPT_PART_2} "${text}"`
}

export async function getSuggestions(
  inputText: string,
  reply?: string,
): Promise<string> {
  try {
    Toast.show(`Analyzing... (${GENAI_PROMPT_VERSION})`)

    const FULL_GENAI_PROMPT = GENAI_PROMPT(inputText, reply)

    console.log('CSky: This is full prompt i am using\n', FULL_GENAI_PROMPT)

    const response = await fetch(
      'https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-pro:generateContent',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-goog-api-key': GEMINI_API_KEY,
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: FULL_GENAI_PROMPT,
                },
              ],
            },
          ],
          safetySettings: [
            {
              category: 'HARM_CATEGORY_HARASSMENT',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE',
            },
            {
              category: 'HARM_CATEGORY_HATE_SPEECH',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE',
            },
            {
              category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE',
            },
            {
              category: 'HARM_CATEGORY_DANGEROUS_CONTENT',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE',
            },
          ],
        }),
      },
    )

    if (!response.ok) {
      const errorData = await response.json()
      if (errorData.error?.message?.includes('blocked due to safety')) {
        throw new Error(
          'Unable to suggest revisions for this content. Please ensure the text follows community guidelines.',
        )
      }
      throw new Error(errorData.error?.message || 'Failed to get suggestions')
    }

    const data = await response.json()

    if (!data.candidates || data.candidates.length === 0) {
      throw new Error(
        'No suggestions available. Please try rephrasing your content.',
      )
    }

    if (data.candidates[0].finishReason === 'SAFETY') {
      throw new Error(
        'Unable to suggest revisions for this content. Please ensure the text follows community guidelines.',
      )
    }

    return data.candidates[0].content.parts[0].text
  } catch (error) {
    console.error('Gemini API error:', error)
    throw error
  }
}

/* const getSuggestions = async (text: string): Promise<string> => {
  try {
    const response = await fetch('https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-goog-api-key': GEMINI_API_KEY,
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: `Please suggest professional and constructive improvements to make this text more engaging while maintaining appropriate content and the same core meaning. Focus only on improving clarity and engagement. Here's the text: "${text}". Provide only the revised text without any explanations.`
          }]
        }],
        safetySettings: [
          {
            category: "HARM_CATEGORY_HARASSMENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_HATE_SPEECH",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_DANGEROUS_CONTENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          }
        ]
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      if (errorData.error?.message?.includes('blocked due to safety')) {
        throw new Error('Unable to suggest revisions for this content. Please ensure the text follows community guidelines.');
      }
      throw new Error(errorData.error?.message || 'Failed to get suggestions');
    }

    const data = await response.json();
    
    // Check for safety blocks in the response
    if (!data.candidates || data.candidates.length === 0) {
      throw new Error('No suggestions available. Please try rephrasing your content.');
    }

    if (data.candidates[0].finishReason === 'SAFETY') {
      throw new Error('Unable to suggest revisions for this content. Please ensure the text follows community guidelines.');
    }

    return data.candidates[0].content.parts[0].text;
  } catch (error) {
    console.error('Gemini API error:', error);
    throw error;
  }
}; */

/* 
// Update the ComposerFooter component's error handling:
const handleAnalyze = async () => {
  try {
    const text = post.richtext.text;
    
    if (!text.trim()) {
      Toast.show(_(msg`Please enter some text first`));
      return;
    }

    Toast.show(_(msg`Analyzing text...`));
    
    // Get both results
    const [toxicityScore, suggestion] = await Promise.all([
      analyzeText(text).catch(error => {
        console.error('Toxicity analysis error:', error);
        return null;
      }),
      getSuggestions(text).catch(error => {
        console.error('Suggestions error:', error);
        return null;
      })
    ]);

    if (!toxicityScore && !suggestion) {
      Toast.show(_(msg`Analysis failed. Please try again with different text.`));
      return;
    }

    // Store results and show dialog
    setAnalysisResults({
      toxicityScore: toxicityScore || 0,
      suggestion: suggestion || 'No suggestions available for this content.'
    });
    analysisDialogControl.open();
    
  } catch (error: any) {
    console.error('Analysis error:', error);
    Toast.show(_(msg`${error.message || 'Analysis failed. Please try again.'}`));
  }
}; */
